"use client";

import type { StartupFilters, TargetAudience } from "@/lib/types";

type Props = {
  filters: StartupFilters;
  countries: string[];
  paymentProviders: string[];
  onChange: (next: StartupFilters) => void;
};

const TARGET_AUDIENCE_OPTIONS: Array<{ value: TargetAudience | "all"; label: string }> = [
  { value: "all", label: "All audiences" },
  { value: "b2b", label: "B2B" },
  { value: "b2c", label: "B2C" },
  { value: "both", label: "Both" }
];

export function StartupFilterBar({ filters, countries, paymentProviders, onChange }: Props) {
  return (
    <div className="filters filters--startup">
      <div className="filter-group filter-group--grow">
        <span className="filter-label">Search</span>
        <input
          className="filter-input"
          type="text"
          placeholder="Startup name or description"
          value={filters.query}
          onChange={(e) => onChange({ ...filters, query: e.target.value })}
        />
      </div>

      <div className="filter-group">
        <span className="filter-label">Audience</span>
        <select
          className="filter-select"
          value={filters.targetAudience ?? "all"}
          onChange={(e) =>
            onChange({ ...filters, targetAudience: e.target.value as StartupFilters["targetAudience"] })
          }
        >
          {TARGET_AUDIENCE_OPTIONS.map((option) => (
            <option key={option.value ?? "all"} value={option.value ?? ""}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-group">
        <span className="filter-label">Country</span>
        <select
          className="filter-select"
          value={filters.country}
          onChange={(e) => onChange({ ...filters, country: e.target.value })}
        >
          <option value="">All countries</option>
          {countries.map((country) => (
            <option key={country} value={country}>
              {country}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-group">
        <span className="filter-label">Provider</span>
        <select
          className="filter-select"
          value={filters.paymentProvider}
          onChange={(e) => onChange({ ...filters, paymentProvider: e.target.value })}
        >
          <option value="">All providers</option>
          {paymentProviders.map((provider) => (
            <option key={provider} value={provider}>
              {provider}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-group">
        <span className="filter-label">Growth band</span>
        <select
          className="filter-select"
          value={filters.growthBand}
          onChange={(e) =>
            onChange({ ...filters, growthBand: e.target.value as StartupFilters["growthBand"] })
          }
        >
          <option value="all">All growth</option>
          <option value="hyper">25%+ growth</option>
          <option value="positive">Positive growth</option>
          <option value="flat">Flat</option>
          <option value="negative">Negative growth</option>
        </select>
      </div>

      <div className="filter-group">
        <span className="filter-label">MRR band</span>
        <select
          className="filter-select"
          value={filters.mrrBand}
          onChange={(e) =>
            onChange({ ...filters, mrrBand: e.target.value as StartupFilters["mrrBand"] })
          }
        >
          <option value="all">All MRR</option>
          <option value="under1k">Under $1k</option>
          <option value="1kto5k">$1k to $5k</option>
          <option value="5kto10k">$5k to $10k</option>
          <option value="10kplus">$10k+</option>
        </select>
      </div>

      <button
        type="button"
        className={`filter-toggle ${filters.onSaleOnly ? "filter-toggle--active" : ""}`}
        onClick={() => onChange({ ...filters, onSaleOnly: !filters.onSaleOnly })}
      >
        For sale only
      </button>
    </div>
  );
}
